import { AxiosResponse } from "axios";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { SidePanelBase, SidePanelContent, SidePanelFooter, SidePanelHeader } from "./SidePanel";
import { Message } from "semantic-ui-react";

interface DetailSidePanelProps<T> {
  apiCall: () => Promise<AxiosResponse<server.dto.ApiResponseWrapper<T>, any>>
  apiKey: string
  entityId: number
  title: string
  titleFunc?: (data: T) => string
  contentClassName?: string
  footerContent: JSX.Element | ((data: T) => JSX.Element)

  onOpen?: () => void
  trigger?: JSX.Element

  children: (data: T, refetch: () => Promise<void>) => JSX.Element

  // This is used when we open panenls imperatively
  skipSidePanelBase?: boolean

  detailUrl?: string
}

export const getApiKeyForSidePanel = (apiKey: string, entityId: number) => {
  return [apiKey, "side", entityId];
}

const DetailSidePanelContent = <T extends any>(props: DetailSidePanelProps<T>) => {
  const api = useApi(() => props.apiCall(), getApiKeyForSidePanel(props.apiKey, props.entityId), {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [props.entityId]);

  const refetch = async () => {
    await api.refetch();
  }

  if (api.isInitialLoading) {
    return <>
      <SidePanelHeader title={props.title} />
      <SidePanelContent>
        <PlaceholderContent />
      </SidePanelContent>
      <SidePanelFooter />
    </>
  }

  if (api.error) {
    // @ts-expect-error this is AxiosError
    const errorCode = api.error?.response?.status || 500;
    return <>
      <SidePanelHeader title={props.title} />
      <SidePanelContent>
        <Message color="red">
          {errorCode === 403 ? RESX.HttpError.Error403Description : RESX.HttpError.Error500Description}
        </Message>
      </SidePanelContent>
      <SidePanelFooter />
    </>
  }

  if (api.data.hasErrors) {
    return <>
      <SidePanelHeader title={props.title} />
      <SidePanelContent>
        <Message color="red">
          {api.data.errors[0].message}
        </Message>
      </SidePanelContent>
      <SidePanelFooter />
    </>
  }

  return <>
    <SidePanelHeader title={props.titleFunc ? props.titleFunc(api.data.data) : props.title} />
    <SidePanelContent className={props.contentClassName}>
      {props.children(api.data.data, refetch)}
    </SidePanelContent>
    <SidePanelFooter>
      {props.footerContent instanceof Function ? props.footerContent(api.data.data) : props.footerContent}
    </SidePanelFooter>
  </>
}

export const DetailSidePanel = <T extends any>(props: DetailSidePanelProps<T>) => {
  if (props.skipSidePanelBase) {
    return <DetailSidePanelContent {...props} />;
  }

  return <SidePanelBase
    onOpen={props.onOpen}
    trigger={props.trigger}
    size="medium"
    ctrlClickUrl={props.detailUrl}
    id={`${props.apiKey}-side-${props.entityId}`}
  >
    <DetailSidePanelContent {...props} />
  </SidePanelBase>
}